import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { AuscultationInformationDefinition } from '../../../../../../entity-definitions/auscultation-information.definition';
import { AuscultationInformation, AuscultationInformationStore } from '../../../../../../stores/auscultation-info.store';
import { EntityFormValue } from '../../../../../../types/entity-form-result.interface';
import { RouteUtils } from '../../../../../../utils/route.utils';
import { ToastService } from '../../../../../../services/toast.service';
import { AuscultationInformationFormComponent } from '../../../../../forms/auscultation-information-form.component';
import { CesAssetBreadcrumbComponent } from '../../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';

@Component({
  selector: 'app-auscultation-info-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AuscultationInformationFormComponent, CesAssetBreadcrumbComponent, TranslocoModule],
  templateUrl: './auscultation-info-page.component.html',
})
export class AuscultationInfoPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslocoService);
  private readonly store = inject(AuscultationInformationStore);
  private readonly toastService = inject(ToastService);

  protected readonly assetId = signal<number | null>(null);
  protected readonly ready = signal(false);
  private readonly translations = toSignal(this.translate.selectTranslation());

  readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    this.translations();
    return [
      {
        label: this.translate.translate('navigation.measures'),
        routerLink: ['/assets', this.assetId(), 'configuration', 'measures', 'auscultation-information'],
      },
      { label: this.translate.translate(AuscultationInformationDefinition.messages.dialogTitles.create()) },
    ];
  });

  readonly title = computed(() => {
    this.translations();
    return this.translate.translate(AuscultationInformationDefinition.messages.dialogTitles.create());
  });

  ngOnInit(): void {
    this.assetId.set(RouteUtils.getAssetIdFromRoute(this.route));
    this.ready.set(true);
  }

  protected async onFormSubmit(value: EntityFormValue<AuscultationInformation>): Promise<void> {
    try {
      await this.store.create(value);
      void this.navigateToList();
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      this.toastService.error('common.errors.saveFailed');
      console.error(this.translate.translate('common.errors.saveFailed'), msg);
    }
  }

  protected onFormCancel(): void {
    void this.navigateToList();
  }

  private async navigateToList(): Promise<void> {
    const id = this.assetId();
    if (!id) {
      await this.router.navigate(['/']);
      return;
    }
    await this.router.navigate(['/assets', id, 'configuration', 'measures', 'auscultation-information']);
  }
}
